import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { AxiosRequestConfig } from "axios";
import type { LoaderType } from "@/shared/loading";
import type {
  CreateChatSessionRequest,
  CreateChatSessionResponse,
  SendMessageRequest,
  SendMessageResponse,
  GetMessagesResponse,
  GetChatSessionsResponse,
  GetAvailableAdminResponse,
  CreateConversationResponse,
} from "@/features/settings/modal/chatSettings.types";

const toFile = async (uri: string, index: number): Promise<File> => {
  const response = await fetch(uri);
  const blob = await response.blob();
  const ext = blob.type.includes("png") ? "png" : "jpg";
  return new File([blob], `attachment-${index + 1}.${ext}`, { type: blob.type || "image/jpeg" });
};

export const chatService = {
  async createConversation(): Promise<CreateConversationResponse> {
    const { data } = await apiClient.post<CreateConversationResponse>(
      API_ENDPOINTS.CHAT.CONVERSATIONS
    );
    return data;
  },

  async getAvailableAdmin(): Promise<ApiResponse<GetAvailableAdminResponse>> {
    const { data } = await apiClient.get<ApiResponse<GetAvailableAdminResponse>>(
      API_ENDPOINTS.CHAT.AVAILABLE_ADMINS
    );
    return data;
  },

  async createChatSession(
    payload: CreateChatSessionRequest
  ): Promise<ApiResponse<CreateChatSessionResponse>> {
    const { data } = await apiClient.post<ApiResponse<CreateChatSessionResponse>>(
      API_ENDPOINTS.CHAT.SESSIONS,
      payload
    );
    return data;
  },

  async getChatSessions(): Promise<ApiResponse<GetChatSessionsResponse>> {
    const response = await apiClient.get<ApiResponse<GetChatSessionsResponse>>(
      API_ENDPOINTS.CHAT.SESSIONS,
      {
        metadata: {
          loaderType: "inline",
        },
      } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } }
    );
    return response.data;
  },

  async getMessages(sessionId: string): Promise<ApiResponse<GetMessagesResponse>> {
    const response = await apiClient.get<ApiResponse<GetMessagesResponse>>(
      API_ENDPOINTS.CHAT.CONVERSATION_MESSAGES(sessionId),
      {
        metadata: {
          loaderType: "inline",
        },
      } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } }
    );
    return response.data;
  },

  async sendMessage(payload: SendMessageRequest): Promise<ApiResponse<SendMessageResponse>> {
    const formData = new FormData();
    formData.append("conversation_id", payload.conversation_id.toString());
    if (payload.text) {
      formData.append("text", payload.text);
    }
    if (payload.fileUris && payload.fileUris.length > 0) {
      const files = await Promise.all(payload.fileUris.map((uri, index) => toFile(uri, index)));
      files.forEach((file) => formData.append("files", file));
    }
    const { data } = await apiClient.post<ApiResponse<SendMessageResponse>>(
      API_ENDPOINTS.CHAT.MESSAGES,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  async subscribeToMessages(
    _sessionId: string,
    _callback: (message: unknown) => void
  ): Promise<() => void> {
    return () => {};
  },
};
